import React from 'react';
import Helmet from 'react-helmet';
import { useLocation } from 'react-router';
import LegacyPanel from '../../../legacyCore/LegacyPanel';
import StaticMap from '../../../legacyCore/StaticMap';
import AppointmentInstructions from '../../../shared/components/AppointmentDetail/AppointmentInstructions';
import AppointmentInfo from '../../../shared/components/AppointmentInfo';
import { createSchedulingSessionStateLandingPage } from '../../../shared/components/SchedulingSessionState';
import Unauthorized from '../../../shared/components/Unauthorized';
import { FLASH_MESSAGES } from '../../../shared/constants';
import { isUnavailable } from '../../../shared/utils/appointmentUtils';
import isForbidden from '../../../shared/utils/isForbidden';
import { buildRescheduleUrl, getQueryFromLocation } from '../../../shared/utils/urls';
import { Redirect } from '../../../_universal';
import Header from '../../components/WidgetHeader';
import WidgetLayout from '../../components/WidgetLayout';
import LoadingPage from '../LoadingPage';
import NotFoundPage from '../NotFoundPage';
import style from './AppointmentDetailPage.scss';

const CustomRedirect: any = Redirect;

interface AppointmentDetailPageProps {
  data: {
    appointment: any;
  };
  loading: boolean;
  error?: Error | null;
}

function AppointmentDetailPage({ data, loading, error }: AppointmentDetailPageProps) {
  const location = useLocation();
  const locationQuery = getQueryFromLocation(location);

  if (loading) return <LoadingPage />;
  if (isForbidden(error))
    return (
      <Unauthorized>
        <LoadingPage />
      </Unauthorized>
    );
  if (error) return <WidgetLayout error={error} />;
  if (!data || !data.appointment) return <NotFoundPage />;

  const { appointment } = data;
  if (isUnavailable(appointment)) {
    const rescheduleUrl = buildRescheduleUrl(appointment.id, {
      patientToken: appointment.patientToken,
    });
    return (
      <CustomRedirect to={rescheduleUrl}>
        <LoadingPage />
      </CustomRedirect>
    );
  }

  return (
    <WidgetLayout
      header={<Header title={`${appointment.patientName}’s Appointment`} showBackButton={false} />}
      appointmentPartner={appointment.partner}
    >
      <Helmet>
        <title>{`${appointment.patientName}'s Appointment`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className={style['appointment-form-page__row']}>
        <div className={style['appointment-form-page__main']}>
          <LegacyPanel mb={0}>
            <AppointmentInstructions
              justScheduled={locationQuery.message === FLASH_MESSAGES.SCHEDULED}
              justConfirmed={locationQuery.message === FLASH_MESSAGES.CONFIRMED}
              appointment={appointment}
              isWidget
            />
          </LegacyPanel>
        </div>

        <div className={style['appointment-form-page__aside']}>
          <StaticMap
            className={style['appointment-form-page__map']}
            width="100%"
            height="192"
            zoom={12}
            markers={[
              {
                latitude: appointment.provider.office.latitude,
                longitude: appointment.provider.office.longitude,
              },
            ]}
          />
          <LegacyPanel px={0} mb={0}>
            <AppointmentInfo appointment={appointment} />
          </LegacyPanel>
        </div>
      </div>
    </WidgetLayout>
  );
}

export default createSchedulingSessionStateLandingPage(AppointmentDetailPage);
